'use client';

import type { PropsWithChildren } from 'react';

import AnalyticsContextProvider from '@/components/app/layout/AnalyticsContextProvider';
import { useCspNonce } from '@/components/layout/CspNonceProvider';
import type { AppInitialState } from '@/store';

import PageWrapper, { type PageWrapperProps } from '../../PageWrapper';
import StoreProvider from '../../StoreProvider';

export type PageClientProviderProps = {
	amplitudeExperimentsDeploymentKey?: string;
	initialState: AppInitialState;
	// Sourced from CspNonceProvider (pinned once at the root layout) below, not
	// accepted from the caller — see PageServerProvider for why threading it
	// through per-page props instead would re-fetch a fresh value on every soft
	// navigation.
} & Omit<PageWrapperProps, 'nonce'>;

const PageClientProvider = ({
	hideAcceptCookiesModal,
	amplitudeExperimentsDeploymentKey,
	amplitudeToken,
	analyticsPageName,
	defaultAuthPrompt,
	initialState,
	children,
}: PropsWithChildren<PageClientProviderProps>) => {
	const nonce = useCspNonce();

	return (
		<StoreProvider initialState={initialState}>
			<AnalyticsContextProvider
				amplitudeExperimentsDeploymentKey={amplitudeExperimentsDeploymentKey}
				amplitudeToken={amplitudeToken}
			>
				<PageWrapper
					amplitudeToken={amplitudeToken}
					analyticsPageName={analyticsPageName}
					defaultAuthPrompt={defaultAuthPrompt}
					hideAcceptCookiesModal={hideAcceptCookiesModal}
					nonce={nonce}
				>
					{children}
				</PageWrapper>
			</AnalyticsContextProvider>
		</StoreProvider>
	);
};

export default PageClientProvider;
